import { useState, useEffect } from "react";
import { TextInput, Combobox, useCombobox, Text, Loader } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { searchCandidates } from "../api";

/**
 * Search-and-pick a candidate. Debounced typeahead; calls onSelect(candidate)
 * when a row is chosen. Clears its own query after each pick. Pass excludeIds
 * to hide candidates already in the parent's list (e.g. self, already-added).
 *
 * Uses Mantine Combobox so the dropdown is a floating popover (portal-rendered,
 * auto-flips above the input when there's no room below).
 */
export default function CandidatePicker({
  onSelect,
  excludeIds = [],
  placeholder,
  disabled,
  size = "xs",
  autoFocus = false,
}) {
  const { t } = useTranslation("Veft");
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (!q) { setResults([]); return; }
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const found = await searchCandidates(q);
        const filtered = (found ?? []).filter((c) => !excludeIds.includes(c.id));
        setResults(filtered.slice(0, 8));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [query, excludeIds.join(",")]);

  const resultById = Object.fromEntries(results.map((c) => [c.id, c]));

  function handleSubmit(id) {
    const c = resultById[id];
    if (!c) return;
    onSelect(c);
    setQuery("");
    setResults([]);
    combobox.closeDropdown();
  }

  const options = results.map((c) => (
    <Combobox.Option key={c.id} value={c.id}>
      <Text size="sm" fw={500} truncate>{c.name}</Text>
      <Text size="xs" c="dimmed" truncate>
        {c.title}{c.career?.[0]?.companyName ? ` at ${c.career[0].companyName}` : ""}
      </Text>
    </Combobox.Option>
  ));

  return (
    <Combobox store={combobox} onOptionSubmit={handleSubmit} withinPortal>
      <Combobox.Target>
        <TextInput
          size={size}
          placeholder={placeholder ?? t("search_candidates")}
          leftSection={<IconSearch size={12} />}
          rightSection={searching ? <Loader size={12} /> : null}
          value={query}
          onChange={(e) => {
            const v = e.currentTarget.value;
            setQuery(v);
            if (v.trim()) combobox.openDropdown();
            else combobox.closeDropdown();
          }}
          onFocus={() => { if (query.trim()) combobox.openDropdown(); }}
          onBlur={() => combobox.closeDropdown()}
          disabled={disabled}
          autoFocus={autoFocus}
        />
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Options>
          {options.length > 0
            ? options
            : !searching && query.trim()
              ? <Combobox.Empty>{t("no_candidate_matches")}</Combobox.Empty>
              : null}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
